import { useState, useEffect, useCallback } from 'react';
import { createContextHookValue } from '@texas/utils/contextState';
import {
  FilterOptionIdsQuery,
  ProductApprovalState,
  ProductApprovalsSearchQuery,
  productApprovalsApi,
} from '@texas/api/endpoints/productApprovalsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  SampleLifecycle,
  lifecycleToGroup,
} from '@texas/components/shared/samples/types';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { getEnumValues } from '@texas/utils/helpers/enumHelpers';
import { queryStringToObject } from '@texas/utils/helpers/commonHelpers';

interface Props {
  customerId: number;
}

export const defaultPageSize = 50;
export const sampleLifecycles = getEnumValues<SampleLifecycle>(SampleLifecycle);
function useCustomerProductApprovalState({ customerId }: Props) {
  const searchQueryParams: FilterOptionIdsQuery | undefined =
    queryStringToObject();
  const {
    data: productApprovals,
    refetch,
    error,
    loading,
  } = useApiResource(productApprovalsApi.getAll);

  const {
    data: customerFilters,
    refetch: refetchCustomerFilters,
    loading: loadingCustomerFilters,
    error: customerFiltersError,
  } = useApiResource(productApprovalsApi.getCustomerFilters);
  const [searchQuery, setSearchQuery] = useState<ProductApprovalsSearchQuery>({
    customerId,
    sampleLifecycles:
      searchQueryParams?.sampleState !== undefined
        ? sampleLifecycles.filter(
            (lifecycle) =>
              lifecycleToGroup[lifecycle] ===
              lifecycleToGroup[searchQueryParams.sampleState!],
          )
        : undefined,
    sortDesc: false,
    sortBy: 'formatNumber',
    searchTerm: searchQueryParams?.searchTerm ?? '',
    page: 1,
    pageSize: defaultPageSize,
    branchIds: searchQueryParams?.branchId
      ? [searchQueryParams.branchId]
      : undefined,
    supplierIds: searchQueryParams?.supplierId
      ? [searchQueryParams.supplierId]
      : undefined,
    state: searchQueryParams?.state ?? ProductApprovalState.Pending,
  });

  const triggerRefetchAll = useCallback(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  useEffect(() => {
    refetchCustomerFilters(customerId);
  }, [customerId, refetchCustomerFilters]);

  useEffect(() => {
    return productApprovalEvents.productApprovalsCreated.subscribe(() => {
      triggerRefetchAll();
    });
  }, [triggerRefetchAll]);

  useEffect(() => {
    return productApprovalEvents.productApprovalChanged.subscribe(({ id }) => {
      if (productApprovals?.allIds?.includes(id)) triggerRefetchAll();
    });
  }, [productApprovals?.allIds, triggerRefetchAll]);

  return {
    productApprovals,
    error,
    loading,
    customerFilters,
    loadingCustomerFilters,
    customerFiltersError,
    searchQuery,
    setSearchQuery,
    triggerRefetchAll,
  };
}

export const [CustomerProductApprovalsProvider, useCustomerProductApprovals] =
  createContextHookValue(useCustomerProductApprovalState);
